import { useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../lib/useFetch";
import { api, API, asList, errorText, money } from "../lib/api";

const STATUSES = ["Received", "Under review", "Revisions requested", "Accepted", "Rejected"];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function Empty({ text }) { return <p className="py-16 text-center text-[var(--muted)]">{text}</p>; }

export function Overview() {
  const { data } = useFetch("/admin/stats", {});
  const s = data || {};
  const cards = [["Articles", s.articles], ["Submissions", s.submissions], ["Awaiting decision", s.pending_submissions], ["Unread messages", s.messages], ["Newsletter subscribers", s.subscribers], ["Paid orders", s.orders], ["Revenue (test mode)", money(s.revenue)]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border rule" data-testid="admin-overview">
      {cards.map(([l, v]) => <div key={l} className="bg-[var(--paper)] p-6"><p className="eyebrow">{l}</p><p className="f-serif text-4xl mt-3" data-testid={`stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v ?? 0}</p></div>)}
    </div>
  );
}

async function download(id, name) {
  try {
    const r = await api.get(`/admin/submissions/${id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(errorText(e)); }
}

export function Submissions() {
  const { data, setData } = useFetch("/admin/submissions", []);
  const list = asList(data);
  const update = async (id, status) => {
    try { await api.patch(`/admin/submissions/${id}`, { status }); setData(list.map((s) => (s.id === id ? { ...s, status } : s))); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(errorText(e)); }
  };
  if (!list.length) return <Empty text="No manuscripts yet." />;
  return (
    <div className="divide-y rule border-y rule" data-testid="admin-submissions">
      {list.map((s) => (
        <div key={s.id} className="py-6 grid lg:grid-cols-12 gap-4" data-testid={`submission-${s.id}`}>
          <div className="lg:col-span-8">
            <p className="f-mono text-xs text-[var(--muted)]">{s.reference} · {fmt(s.created_at)} · {s.manuscript_type} · {s.category}</p>
            <p className="f-serif text-2xl mt-1">{s.title}</p>
            <p className="text-sm mt-1">{s.authors} — {s.affiliation} · <a href={`mailto:${s.email}`} className="link-u">{s.email}</a>{s.orcid ? ` · ORCID ${s.orcid}` : ""}</p>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed line-clamp-3">{s.abstract}</p>
          </div>
          <div className="lg:col-span-4 flex lg:flex-col lg:items-end gap-3">
            <select value={s.status} onChange={(e) => update(s.id, e.target.value)} className="border rule bg-white h-10 px-3 text-sm" data-testid={`submission-status-${s.id}`}>{STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
            {s.file_name && <button onClick={() => download(s.id, s.file_name)} className="flex items-center gap-2 text-sm link-u" data-testid={`submission-download-${s.id}`}><Download size={15} /> {s.file_name}</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Messages() {
  const { data, setData } = useFetch("/admin/messages", []);
  const list = asList(data);
  const read = async (id) => { await api.patch(`/admin/messages/${id}/read`).catch(() => {}); setData(list.map((m) => (m.id === id ? { ...m, read: true } : m))); };
  if (!list.length) return <Empty text="No messages yet." />;
  return (
    <div className="divide-y rule border-y rule" data-testid="admin-messages">
      {list.map((m) => (
        <div key={m.id} className="py-5 flex gap-4" data-testid={`message-${m.id}`}>
          <span className={`mt-2 h-2 w-2 rounded-full shrink-0 ${m.read ? "bg-transparent" : "bg-[var(--red)]"}`} />
          <div className="flex-1">
            <p className="f-mono text-xs text-[var(--muted)]">{fmt(m.created_at)} · {m.subject}</p>
            <p className="font-semibold mt-1">{m.name} · <a href={`mailto:${m.email}`} className="link-u font-normal">{m.email}</a></p>
            <p className="text-[var(--ink-2)] mt-2 whitespace-pre-wrap">{m.message}</p>
          </div>
          {!m.read && <button onClick={() => read(m.id)} className="text-sm link-u self-start" data-testid={`message-read-${m.id}`}>Mark read</button>}
        </div>
      ))}
    </div>
  );
}

export function Subscribers() {
  const { data } = useFetch("/admin/subscribers", []);
  const list = asList(data);
  const csv = () => {
    const blob = new Blob([["email,joined", ...list.map((s) => `${s.email},${s.created_at}`)].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "bpj-subscribers.csv"; a.click();
  };
  return (
    <div data-testid="admin-subscribers">
      <div className="flex justify-between items-center mb-6"><p className="f-serif text-2xl">{list.length} subscribers</p>{list.length > 0 && <button onClick={csv} className="btn-line h-10 px-4 text-sm flex items-center gap-2" data-testid="export-subscribers-btn"><Download size={15} /> Export CSV</button>}</div>
      {!list.length ? <Empty text="No subscribers yet." /> : <ul className="divide-y rule border-y rule">{list.map((s) => <li key={s.email} className="py-3 flex justify-between text-sm"><span>{s.email}</span><span className="f-mono text-[var(--muted)]">{fmt(s.created_at)}</span></li>)}</ul>}
    </div>
  );
}

export function Orders() {
  const { data } = useFetch("/admin/orders", []);
  const list = asList(data);
  if (!list.length) return <Empty text="No orders yet." />;
  return (
    <div className="overflow-x-auto" data-testid="admin-orders">
      <table className="w-full text-sm">
        <thead><tr className="text-left eyebrow border-b border-[var(--ink)]"><th className="py-3">Date</th><th>Items</th><th>Type</th><th>Customer</th><th>Amount</th><th>Status</th></tr></thead>
        <tbody className="divide-y rule">
          {list.map((o) => (
            <tr key={o.session_id}><td className="py-3 f-mono">{fmt(o.created_at)}</td><td>{asList(o.items).map((i) => `${i.lookup_key} ×${i.quantity}`).join(", ")}</td><td>{o.mode}</td><td>{o.customer_email || "—"}</td><td className="f-mono">{money(o.amount)}</td>
              <td><span className={`px-2 py-0.5 text-xs ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-[var(--paper-2)]"}`}>{o.payment_status}</span></td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BLANK = { title: "", dek: "", type: "news", category: "Strategy", author: "", affiliation: "", issue_id: "", doi: "", read_time: 6, image: "/img/u1551288049-bebda4e38f71.jpg", body: "" };

export function ArticlesAdmin() {
  const { data, setData } = useFetch("/articles", []);
  const list = asList(data);
  const [f, setF] = useState(BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, read_time: Number(f.read_time) || 6, issue_id: f.issue_id || null, doi: f.doi || null, body: f.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) };
      const { data: a } = await api.post("/admin/articles", payload);
      setData([a, ...list]); setF(BLANK); toast.success("Article published");
    } catch (err) { toast.error(errorText(err)); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${slug}`); setData(list.filter((a) => a.slug !== slug)); toast.success("Deleted"); }
    catch (err) { toast.error(errorText(err)); }
  };
  return (
    <div className="grid lg:grid-cols-12 gap-12" data-testid="admin-articles">
      <form onSubmit={create} className="lg:col-span-5 space-y-5" data-testid="article-create-form">
        <p className="f-serif text-2xl">Publish an article</p>
        <input required placeholder="Title" value={f.title} onChange={set("title")} className="field" data-testid="new-article-title" />
        <input required placeholder="Standfirst / summary" value={f.dek} onChange={set("dek")} className="field" data-testid="new-article-dek" />
        <div className="grid grid-cols-2 gap-4">
          <select value={f.type} onChange={set("type")} className="field" data-testid="new-article-type"><option value="news">Analysis</option><option value="research">Peer-reviewed</option></select>
          <select value={f.category} onChange={set("category")} className="field" data-testid="new-article-category">{["Strategy", "Growth", "People", "Finance", "Leadership", "Operations", "Technology", "Macroeconomics"].map((c) => <option key={c}>{c}</option>)}</select>
          <input required placeholder="Author" value={f.author} onChange={set("author")} className="field" data-testid="new-article-author" />
          <input placeholder="Affiliation" value={f.affiliation} onChange={set("affiliation")} className="field" />
          <input placeholder="DOI (research)" value={f.doi} onChange={set("doi")} className="field" />
          <input placeholder="Issue id e.g. v14n4" value={f.issue_id} onChange={set("issue_id")} className="field" />
        </div>
        <input placeholder="Image URL" value={f.image} onChange={set("image")} className="field" />
        <textarea required rows={7} placeholder="Body — separate paragraphs with a blank line" value={f.body} onChange={set("body")} className="field resize-none" data-testid="new-article-body" />
        <button className="btn-red h-11 px-8 font-semibold" data-testid="new-article-submit">Publish</button>
      </form>
      <div className="lg:col-span-7 divide-y rule border-y rule self-start">
        {list.map((a) => (
          <div key={a.slug} className="py-4 flex items-center justify-between gap-4" data-testid={`admin-article-${a.slug}`}>
            <div><p className="f-mono text-xs text-[var(--muted)]">{a.type} · {a.category}</p><a href={`/article/${a.slug}`} className="f-serif text-lg link-u">{a.title}</a></div>
            <button onClick={() => remove(a.slug)} className="p-2 text-[var(--muted)] hover:text-[var(--red)]" aria-label="Delete" data-testid={`delete-article-${a.slug}`}><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export const _api = API;
